import { Component, EventEmitter, Inject, Input, type OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  HTTPCONSTANTS,
  NotificationService,
  type OdinFeatures,
  type UserProfile,
  type UserProfileSettings,
} from '@odin/odin-core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CognitoService } from '../../services/cognito-service';

class MFA_Option {
  id: 'TOTP' | 'SMS_MFA' | 'EMAIL_OTP';
  icon: string;
  title: string;
  description: string;
  selected: boolean;
}

@Component({
  selector: 'odin-mfa-configurator',
  standalone: false,
  templateUrl: './mfa-configurator.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mfa-configurator.component.css',
})
export class MfaConfiguratorComponent implements OnInit {
  public isActive: boolean = false;

  @Input() public isSelf: boolean = false;
  @Input() public profile: UserProfile;
  @Input() public profileSettings: UserProfileSettings | null;
  @Output() public mfaUpdated = new EventEmitter<void>();

  public totpSecret: string;
  public totpCode: string;
  public smsValidationResponse: {
    message: string;
    verificationRequired: boolean;
    smsDestination: string;
    otp: string;
  };
  public step:
    | 'SELECT_METHOD'
    | 'ENTER_SMS'
    | 'VALIDATE_SMS'
    | 'SETUP_TOTP'
    | 'VERIFY_TOTP' = 'SELECT_METHOD';
  public loading: boolean = false;
  public hasMFAConfigured: boolean = false;
  public selectedMFAOption: MFA_Option;
  public options: MFA_Option[] = [
    {
      id: 'TOTP',
      icon: 'screen_lock_portrait',
      title: 'Mobile App',
      description: 'Use a code from an app to log in',
      selected: false,
    },
    {
      id: 'SMS_MFA',
      icon: 'sms',
      title: 'SMS',
      description: 'Receive verification codes via SMS.',
      selected: false,
    },
    {
      id: 'EMAIL_OTP',
      icon: 'email',
      title: 'Email',
      description: 'Receive verification codes via email.',
      selected: false,
    },
  ];

  constructor(
    private cognitoService: CognitoService,
    private httpClient: HttpClient,
    private notificationService: NotificationService,
    @Inject('features') private featureHelper: OdinFeatures,
  ) {
    if (this.featureHelper['mfa-configuration'] === true) this.isActive = true;
    else this.isActive = false;
  }

  ngOnInit(): void {
    this.resetForm();
  }

  public resetForm(): void {
    this.step = 'SELECT_METHOD';
    this.fetchProfileSettings();
  }

  private fetchProfileSettings(): void {
    if (this.profile && this.profileSettings) {
      // Check if user has MFA configured
      this.hasMFAConfigured = !!this.profileSettings.mfaMethod;
      this.selectMFAOption(
        this.options.find((o) => o.id === this.profileSettings!.mfaMethod) ||
          this.options[0],
        false,
      );
      return;
    }
    
    // Otherwise, fetch current user's profile
    const tempProfile = this.isSelf ? this.cognitoService.userProfile() : this.profile;
    if (!tempProfile) return;
    this.loading = true;
    if (!this.profile) this.profile = tempProfile;
    this.cognitoService.fetchProfileSettings(this.profile.userId).subscribe(
      (settings: UserProfileSettings) => {
        this.profileSettings = settings;
        this.hasMFAConfigured = !!settings.mfaMethod;
        this.selectMFAOption(
          this.options.find((o) => o.id === settings.mfaMethod) ||
            this.options[0],
          false,
        );
        this.loading = false;
      },
      (error: HttpErrorResponse) => {
        this.notificationService.ErrorSmallDialog('Failed to fetch MFA settings', error);
        this.loading = false;
      },
    );
  }

  public selectMFAOption(
    option: MFA_Option,
    continueSteps: boolean = true,
  ): void {
    this.selectedMFAOption = option;
    this.options.forEach((opt) => (opt.selected = opt === option));

    if (!continueSteps) return; // just selecting for UI, not continuing to API steps

    if (this.selectedMFAOption.id === 'SMS_MFA') {
      this.step = 'ENTER_SMS';
      return;
    }

    if (this.selectedMFAOption.id === 'TOTP') {
      this.step = 'SETUP_TOTP';
      this.GetTOTPSetup();
      return;
    }

    if (this.selectedMFAOption.id === 'EMAIL_OTP') {
      this.SetupMFA();
      return;
    }
  }

  // BFF Implementation

  public ResetIndividualsMFA(): void {
    const userId = this.profile.userId;
    const userOrgId = this.profile.organisationId;
    this.httpClient
      .post<{
        message: string;
        verificationRequired: boolean;
        smsDestination: string;
      }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-authentication/${userOrgId}/reset-user-mfa`,
        {
          userId,
        },
      )
      .subscribe(
        () => {
          this.loading = false;
          this.notificationService.SmallDialog(
            'MFA for this user has been reset',
          );
          this.mfaUpdated.emit();
          this.profileSettings = null;
          this.resetForm();
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to reset MFA for this user',
            error,
          );
        },
      );
  }

  public ConfigurePhoneNumber(): void {
    if (
      !this.profileSettings?.phoneNumber ||
      this.profileSettings.phoneNumber.length === 0
    ) {
      this.notificationService.SmallDialog(
        'Phone number is required for SMS MFA',
      );
      return;
    }
    this.loading = true;
    const reqBody = {
      phoneNumber: this.ValidatedPhoneNumber(this.profileSettings!.phoneNumber),
    };
    this.httpClient
      .post<{
        message: string;
        verificationRequired: boolean;
        smsDestination: string;
      }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-user-management/register-phone-number`,
        reqBody,
      )
      .subscribe(
        (resp: {
          message: string;
          verificationRequired: boolean;
          smsDestination: string;
        }) => {
          this.loading = false;
          if (resp.verificationRequired) {
            this.step = 'VALIDATE_SMS';
            this.smsValidationResponse = { ...resp, otp: '' };
          } else this.SetupMFA();
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to register phone number',
            error,
          );
        },
      );
  }

  public VerifyPhoneNumber(): void {
    this.loading = true;
    const reqBody = {
      code: this.smsValidationResponse.otp,
    };
    this.httpClient
      .post<{
        verificationResult: string;
      }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-user-management/verify-phone-number`,
        reqBody,
      )
      .subscribe(
        (resp: { verificationResult: string }) => {
          this.loading = false;
          if (resp.verificationResult === 'success') {
            this.SetupMFA();
          } else {
            const message = `Failed to verify phone number. Reason: ${resp.verificationResult}`;
            this.notificationService.ErrorSmallDialog(message, new Error(message));
          }
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to register phone number',
            error,
          );
        },
      );
  }

  private GetTOTPSetup(): void {
    this.loading = true;
    this.httpClient
      .post<{
        secretCode: string;
      }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-authentication/setup-totp-mfa`,
        {},
      )
      .subscribe(
        (resp: { secretCode: string }) => {
          this.loading = false;
          this.totpSecret = this.BuildTotpSecret(
            resp.secretCode,
            this.profile.email,
            'Monek Portal',
          );
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to setup MFA. Please try again.',
            error,
          );
        },
      );
  }

  public VerifyTOTPCode(): void {
    this.loading = true;
    this.httpClient
      .post<{ codeAccepted: boolean }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-authentication/verify-totp-mfa`,
        {
          userCode: this.totpCode,
        },
      )
      .subscribe(
        (resp: { codeAccepted: boolean }) => {
          this.loading = false;
          if (!resp.codeAccepted) {
            const message = 'The code you entered was not accepted. Please try again.';
            this.notificationService.ErrorSmallDialog(message, new Error(message));
            return;
          }
          this.notificationService.SmallDialog(
            'MFA setup complete',
            'Close',
            4000,
          );
          this.mfaUpdated.emit();
          this.profileSettings = null;
          this.resetForm();
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to setup MFA. Please try again.',
            error,
          );
        },
      );
  }

  private SetupMFA(): void {
    this.loading = true;
    let endpoint = `/setup-totp-mfa`;
    if (this.selectedMFAOption.id === 'SMS_MFA') endpoint = `/setup-sms-mfa`;
    if (this.selectedMFAOption.id === 'EMAIL_OTP')
      endpoint = `/setup-email-mfa`;
    this.httpClient
      .post<{
        success: boolean;
      }>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.baseAddress}/portal/odin-authentication/${endpoint}`,
        {},
      )
      .subscribe(
        (resp: { success: boolean }) => {
          this.loading = false;
          if (!resp || resp.success === false) {
            const message = 'Failed to setup MFA. Please try again.';
            this.notificationService.ErrorSmallDialog(message, new Error(message));
            return;
          }
          this.notificationService.SmallDialog(
            'MFA setup complete',
            'Close',
            4000,
          );
          this.mfaUpdated.emit();
          this.profileSettings = null;
          this.resetForm();
        },
        (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.ErrorSmallDialog(
            'Failed to setup MFA. Please try again.',
            error,
          );
        },
      );
  }

  private BuildTotpSecret(
    secret: string,
    accountName: string,
    issuer: string,
  ): string {
    return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
  }

  private ValidatedPhoneNumber(phoneNumber: string): string {
    if (phoneNumber.startsWith('+44')) return phoneNumber; // country code added
    if (phoneNumber.startsWith('0'))
      phoneNumber = `+44${phoneNumber.substring(1)}`;
    return phoneNumber;
  }
}
